import React, { createContext, useState, useEffect } from 'react';
import { auth } from '../firebase';
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import axios from 'axios';
import API_BASE_URL from '../config';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          const userResponse = await axios.get(`${API_BASE_URL}/api/users?firebaseUid=${firebaseUser.uid}`);
          const userData = userResponse.data.data.find(user => user.firebaseUid === firebaseUser.uid);
          if (userData) {
            if (userData.role === 'admin') {
              setUser({ ...firebaseUser, role: 'admin', userId: userData._id, email: userData.email, name: userData.name, lastName: userData.lastName1 });
              setRole('admin');
            } else if (userData.role === 'client') {
              setUser({ ...firebaseUser, role: 'client', clientId: userData._id, userId: userData._id, email: userData.email, name: userData.name, lastName: userData.lastName1 });
              setRole('client');
            }
          } else {
            throw new Error('No user found for UID');
          }
        } else {
          setUser(null);
          setRole(null);
        }
      } catch (error) {
        console.error('onAuthStateChanged error:', error.message);
        setUser(null);
        setRole(null);
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      if (userCredential.user) {
        const userResponse = await axios.get(`${API_BASE_URL}/api/users?firebaseUid=${userCredential.user.uid}`);
        const userData = userResponse.data.data.find(user => user.firebaseUid === userCredential.user.uid);
        if (userData) {
          if (userData.role === 'admin') {
            const userWithRole = { ...userCredential.user, role: 'admin', userId: userData._id, email: userData.email, name: userData.name, lastName: userData.lastName1 };
            setUser(userWithRole);
            setRole('admin');
            return userWithRole;
          } else if (userData.role === 'client') {
            const userWithRole = { ...userCredential.user, role: 'client', clientId: userData._id, userId: userData._id, email: userData.email, name: userData.name, lastName: userData.lastName1 };
            setUser(userWithRole);
            setRole('client');
            return userWithRole;
          }
        } else {
          throw new Error('No user found for UID');
        }
      }
    } catch (error) {
      console.error('Login error:', error.message);
      setUser(null);
      setRole(null);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const register = async (formData) => {
    try {
      // Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      
      if (userCredential.user) {
        // Create user record in database
        const userData = {
          email: formData.email,
          name: formData.name,
          midName: formData.midName,
          lastName1: formData.lastName1,
          lastName2: formData.lastName2,
          dob: formData.dob ? formData.dob.toISOString().split('T')[0] : null
        };
        
        await axios.post(`${API_BASE_URL}/api/clients/with-user`, userData);
        
        // Sign out the user after registration (they'll need to log in)
        await signOut(auth);
        
        return { success: true, message: 'Registration successful! Please log in.' };
      }
    } catch (error) {
      console.error('Registration error:', error.message);
      throw error;
    }
  };

  const logout = async () => {
    await signOut(auth);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, role }}>
      {children}
    </AuthContext.Provider>
  );
};